import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * WorkshopVsCatalogRadar
 *
 * Inline 5-axis radar overlaying the user's owned machine vs the optimal
 * market machine of the same category (from the generic catalog). Lets the
 * workshop owner spot capability gaps at a glance ("la mia macchina ha solo
 * 11kW di mandrino mentre l'optimum di categoria è 18.5kW") so they can
 * justify hardware upgrades or invest where it matters.
 *
 * Props
 *  - userMachine: row from /api/user-machines (spindle_power_kw, spindle_max_rpm, travel_x_mm…)
 *  - catalogMachine: row from /api/catalog/machines, same category_id (spindle_kw, max_torque_nm…)
 */
const AXES = [
  { key: "capacity",  labelKey: "radarChart.axCapacity", refMax: 2000,  unit: "mm" },
  { key: "spindle",   labelKey: "radarChart.axSpindle",  refMax: 45,    unit: "kW" },
  { key: "torque",    labelKey: "radarChart.axTorque",   refMax: 250,   unit: "Nm" },
  { key: "rpm",       labelKey: "radarChart.axRpm",      refMax: 24000, unit: "rpm" },
  { key: "roughing",  labelKey: "radarChart.axRoughing", refMax: 350,   unit: "cm³/min" },
];

const COLORS = {
  user:    { stroke: "#34d399", fill: "rgba(52,211,153,0.22)", dot: "#34d399" }, // emerald
  catalog: { stroke: "#22d3ee", fill: "rgba(34,211,238,0.15)", dot: "#22d3ee" }, // cyan
};

function extractCapacity(m) {
  // Try numeric travel_x / travel_y, otherwise parse leading number from max_workspace_mm
  const tx = parseFloat(m?.travel_x_mm);
  const ty = parseFloat(m?.travel_y_mm);
  if (!isNaN(tx) && tx > 0) return Math.max(tx, isNaN(ty) ? 0 : ty);
  const ws = String(m?.max_workspace_mm || "");
  const match = ws.match(/(\d{2,5})/);
  if (match) return parseFloat(match[1]);
  return 0;
}

function userVector(m) {
  return {
    capacity: extractCapacity(m),
    spindle:  parseFloat(m?.spindle_power_kw || 0),
    torque:   parseFloat(m?.spindle_torque_nm || 0), // optional
    rpm:      parseFloat(m?.spindle_max_rpm || 0),
    roughing: parseFloat(m?.roughing_capacity_cm3_min || 0),
  };
}

function catalogVector(m) {
  return {
    capacity: parseFloat(m?.max_size_mm || 0),
    spindle:  parseFloat(m?.spindle_kw || 0),
    torque:   parseFloat(m?.max_torque_nm || 0),
    rpm:      parseFloat(m?.max_rpm || 0),
    roughing: parseFloat(m?.roughing_capacity_cm3_min || 0),
  };
}

export default function WorkshopVsCatalogRadar({ userMachine, catalogMachine }) {
  const { t } = useTranslation();
  const fromCatalog = useMemo(
    () => new Set(Array.isArray(userMachine?._specs_from_catalog) ? userMachine._specs_from_catalog : []),
    [userMachine],
  );

  const data = useMemo(() => {
    const u = userVector(userMachine);
    const c = catalogVector(catalogMachine);
    const userKeyMap = {
      capacity: "travel_x_mm",
      spindle: "spindle_power_kw",
      torque: "spindle_torque_nm",
      rpm: "spindle_max_rpm",
      roughing: "roughing_capacity_cm3_min",
    };
    return AXES.map((ax) => {
      const uRaw = u[ax.key] || 0;
      const cRaw = c[ax.key] || 0;
      return {
        ...ax,
        userRaw: uRaw,
        catalogRaw: cRaw,
        userNorm: Math.max(0, Math.min(1, uRaw / ax.refMax)),
        catalogNorm: Math.max(0, Math.min(1, cRaw / ax.refMax)),
        delta_pct: cRaw > 0 && uRaw > 0 ? Math.round(((uRaw - cRaw) / cRaw) * 100) : null,
        estimated: fromCatalog.has(userKeyMap[ax.key]),
      };
    });
  }, [userMachine, catalogMachine, fromCatalog]);

  // Aggregate gap: average normalized delta across axes (-1..+1)
  const gap = useMemo(() => {
    const valid = data.filter((d) => d.catalogRaw > 0);
    if (valid.length === 0) return null;
    const sum = valid.reduce((acc, d) => acc + (d.userNorm - d.catalogNorm), 0);
    return Math.round((sum / valid.length) * 100);
  }, [data]);

  if (!catalogMachine) return null;

  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const r = 78;
  const angleFor = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / AXES.length;
  const toXY = (i, norm) => [cx + r * norm * Math.cos(angleFor(i)), cy + r * norm * Math.sin(angleFor(i))];
  const ringPath = (norm) =>
    AXES.map((_, i) => {
      const [x, y] = toXY(i, norm);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ") + " Z";
  const polygonPath = (vector) =>
    data.map((d, i) => {
      const [x, y] = toXY(i, vector === "user" ? d.userNorm : d.catalogNorm);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ") + " Z";

  return (
    <div className="tech-card p-4 mt-3 border-cyan-500/30" data-testid="workshop-vs-catalog-radar">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tech text-cyan-300 text-[10px]">{t("radarChart.workshopVsCatalog")}</div>
        {gap !== null && (
          <span
            className={`text-[10px] label-tech px-2 py-0.5 rounded border font-mono-data ${
              gap >= 0
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                : gap >= -20
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                  : "border-rose-500/40 bg-rose-500/10 text-rose-300"
            }`}
            data-testid="radar-gap-badge"
          >
            {gap >= 0 ? "+" : ""}{gap}% vs categoria
          </span>
        )}
      </div>

      <div className="flex flex-col md:flex-row items-center gap-4">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
          {[0.25, 0.5, 0.75, 1].map((n) => (
            <path key={`r-${n}`} d={ringPath(n)} fill="none" stroke="#334155" strokeWidth={n === 1 ? 1 : 0.5} strokeDasharray={n === 1 ? "0" : "2 3"} />
          ))}
          {AXES.map((ax, i) => {
            const [x, y] = toXY(i, 1);
            return <line key={`ax-${ax.key}`} x1={cx} y1={cy} x2={x} y2={y} stroke="#334155" strokeWidth={0.5}/>;
          })}
          {AXES.map((ax, i) => {
            const [x, y] = toXY(i, 1.22);
            const ang = Math.cos(angleFor(i));
            const anchor = Math.abs(ang) < 0.3 ? "middle" : ang > 0 ? "start" : "end";
            return (
              <text key={`l-${ax.key}`} x={x} y={y} fontSize="8" fill="#94a3b8" textAnchor={anchor} dominantBaseline="middle" fontFamily="ui-monospace, monospace">
                {t(ax.labelKey)}
              </text>
            );
          })}
          {/* Catalog (rendered first so user appears on top) */}
          <path d={polygonPath("catalog")} fill={COLORS.catalog.fill} stroke={COLORS.catalog.stroke} strokeWidth={1.2} strokeDasharray="2 2"/>
          {data.map((d, i) => {
            const [x, y] = toXY(i, d.catalogNorm);
            return <circle key={`c-dot-${i}`} cx={x} cy={y} r={2} fill={COLORS.catalog.dot}/>;
          })}
          {/* User polygon */}
          <path d={polygonPath("user")} fill={COLORS.user.fill} stroke={COLORS.user.stroke} strokeWidth={1.6}/>
          {data.map((d, i) => {
            const [x, y] = toXY(i, d.userNorm);
            return <circle key={`u-dot-${i}`} cx={x} cy={y} r={2.5} fill={COLORS.user.dot}/>;
          })}
        </svg>

        <div className="flex-1 min-w-0 w-full">
          <div className="flex items-center gap-3 mb-2 text-[11px]">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS.user.stroke }}/><span className="text-slate-200">{userMachine?.name || t("radarChart.yourMachine")}</span></span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border border-cyan-400" style={{ background: COLORS.catalog.fill }}/><span className="text-slate-400">{t("radarChart.optimal")} {catalogMachine?.name}</span></span>
          </div>
          <div className="space-y-1">
            {data.map((d) => {
              const hasData = d.catalogRaw > 0;
              const userHas = d.userRaw > 0;
              return (
                <div key={`row-${d.key}`} className="flex items-center justify-between gap-2 text-[10px] font-mono-data text-slate-400" data-testid={`radar-axis-${d.key}`}>
                  <span className="text-slate-500 w-20 shrink-0">{t(d.labelKey)}</span>
                  <span className={userHas ? (d.estimated ? "text-amber-300" : "text-emerald-300") : "text-slate-600"} title={d.estimated ? t("radarChart.estimatedTitle") : t("radarChart.workshopValue")}>
                    {userHas ? `${d.userRaw}` : "—"} {d.unit}
                    {d.estimated && <span className="text-amber-500/80 ml-1" aria-label="stima">~</span>}
                  </span>
                  <span className="text-slate-700">vs</span>
                  <span className={hasData ? "text-cyan-300" : "text-slate-600"}>{hasData ? `${d.catalogRaw}` : "—"} {d.unit}</span>
                  {hasData && d.delta_pct !== null && (
                    <span className={`ml-auto px-1 rounded text-[9px] ${d.delta_pct >= 0 ? "bg-emerald-500/10 text-emerald-300" : d.delta_pct >= -20 ? "bg-amber-500/10 text-amber-300" : "bg-rose-500/10 text-rose-300"}`}>
                      {d.delta_pct >= 0 ? "+" : ""}{d.delta_pct}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 pt-2 border-t border-slate-800 text-[9px] text-slate-500 leading-relaxed">
            Linea piena = la tua macchina · linea tratteggiata = optimum di categoria. Delta ≥ 0% = parità o superiore.
            {fromCatalog.size > 0 && (
              <div className="text-amber-400/80 mt-1">
                ~ I valori con tilde sono stimati dalla categoria catalogo (la tua officina non ha ancora inserito questi dati).
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
